import { Fragment, useEffect, useRef, useState } from 'react';
import { RevealOnView } from '@/components/ui';
import { CONTENT_SLIDE, HEADER_FADE, useInView } from '@/utils/animation';
import { KeyOutcomesCard } from './KeyOutcomesCard';
import type { ArticleSectionData } from './types';

interface ArticleSectionProps {
  section: ArticleSectionData;
  /** When true the section background uses the divider-light/20 tint (alternates with plain oat). */
  tinted?: boolean;
}

// Body + Key outcomes start 50% into the header fade.
const BODY_DELAY_MS = Math.round(HEADER_FADE.durationMs * 0.5);

/**
 * Numbered article section — left column header (number + title + subtitle +
 * Key outcomes card) and right column body paragraphs.
 *
 * Typography is unified with the products page:
 *   - Section title:  Karla Regular 32/32 tracking -0.025em, color clearlab-dusk
 *     (matches the products SectionHeader title).
 *   - Section subtitle: Karla Regular 16/44, color clearlab-dusk
 *     (matches the products SectionHeader intro).
 *   - Body paragraphs: Karla Regular 16/44, color clearlab-dusk
 *     (matches the products product-row description).
 *   - Channel emphasis line: bold label + em-dash + regular text, all 16/44 dusk.
 *
 * Numbered eyebrow `01.` stays Karla Bold 16/32 clearlab-steel — the only piece
 * specific to this template (products has no equivalent).
 *
 * Section bg alternates per index: tinted = #E2DFDA at 20%, else #FAFAFA (oat).
 *
 * Responsive (column layout):
 *   Mobile / md  : 1-col stack (header above body), gap-20 (80px)
 *   lg           : 2-col flex with header-left + body-right (flex-1 each), gap-20 (80px)
 *   xl+          : 2-col `xl:justify-between`, left `xl:w-[440px]` + right
 *                  `xl:w-[600px]` — same widths and column positions as the
 *                  page-level ArticleHeader, so body text aligns vertically
 *                  with the page intro paragraph.
 */
/**
 * Inner content of an `ArticleSection` — left column (eyebrow + title +
 * subtitle + key outcomes) and right column (body paragraphs). Exported
 * separately so `ArticlePage` can embed the first section directly inside
 * the page-level intro `<section>`, sitting right under the divider with
 * just `gap-30` separation. Same pattern Our Story uses where the first
 * row is part of section 1 rather than its own section below.
 */
interface ArticleSectionContentProps {
  section: ArticleSectionData;
  /**
   * When provided, drives the header fade + content slide reveal off a parent's
   * `useInView` boolean. The number / title / subtitle group fades 0 → 1 over
   * `HEADER_FADE` timing; `KeyOutcomesCard` and body paragraphs content-slide
   * up after 50% of the header fade. When `undefined` (default) the content
   * renders statically with no animation — used by `ArticlePage` for the first
   * section, which slides up as one block via its parent wrapper instead.
   */
  visible?: boolean;
}

const renderBody = (section: ArticleSectionData) =>
  section.body.map((block, idx) => {
    const isLast = idx === section.body.length - 1;
    const marginClass = isLast ? '' : 'mb-10';
    if (block.type === 'channel') {
      return (
        <p key={idx} className={marginClass}>
          <span className="font-bold leading-[44px]">{block.label}</span>
          <span className="leading-[44px]">{` — ${block.text}`}</span>
        </p>
      );
    }
    return (
      <Fragment key={idx}>
        <p className={`leading-[44px] ${marginClass}`}>{block.text}</p>
      </Fragment>
    );
  });

export function ArticleSectionContent({ section, visible }: ArticleSectionContentProps) {
  const animated = visible !== undefined;
  const isVisible = visible ?? true;
  const headerStyle = animated
    ? {
        opacity: isVisible ? 1 : 0,
        transition: `opacity ${HEADER_FADE.durationMs}ms ${HEADER_FADE.ease}`,
      }
    : undefined;

  const headerGroup = (
    <div className="flex flex-col items-start gap-8" style={headerStyle}>
      <p className="text-[16px] font-bold leading-8 text-clearlab-steel">{section.number}</p>
      <div className="flex flex-col items-start gap-2">
        <h2 className="text-[32px] font-normal leading-8 tracking-[-0.025em] text-clearlab-dusk">
          {section.title}
        </h2>
        {section.subtitle && (
          <p className="text-[16px] font-normal leading-[44px] text-clearlab-midnight">
            {section.subtitle}
          </p>
        )}
      </div>
    </div>
  );

  const keyOutcomes = <KeyOutcomesCard outcomes={section.keyOutcomes} />;

  const body = (
    <div className="flex flex-1 flex-col text-[16px] font-normal text-clearlab-midnight xl:w-[600px] xl:flex-none">
      {renderBody(section)}
    </div>
  );

  return (
    <div className="flex w-full flex-col gap-20 lg:flex-row lg:items-start xl:justify-between xl:gap-10">
      {/* Left column — eyebrow + title + subtitle + key outcomes */}
      <div className="flex flex-1 flex-col items-start gap-20 lg:pt-2 xl:w-[440px] xl:flex-none">
        {headerGroup}
        {animated ? (
          <RevealOnView
            {...CONTENT_SLIDE}
            inView={isVisible}
            delayMs={BODY_DELAY_MS}
            className="w-full"
          >
            {keyOutcomes}
          </RevealOnView>
        ) : (
          keyOutcomes
        )}
      </div>

      {/* Right column — body paragraphs */}
      {animated ? (
        <RevealOnView
          {...CONTENT_SLIDE}
          inView={isVisible}
          delayMs={BODY_DELAY_MS}
          className="flex flex-1 flex-col text-[16px] font-normal text-clearlab-midnight xl:w-[600px] xl:flex-none"
        >
          {renderBody(section)}
        </RevealOnView>
      ) : (
        body
      )}
    </div>
  );
}

export function ArticleSection({ section, tinted = false }: ArticleSectionProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const inView = useInView(sectionRef, { threshold: 0 });
  // 200ms beat after the section first enters view before the cascade fires —
  // matches the products-page section trigger.
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!inView) return;
    const t = setTimeout(() => setVisible(true), 200);
    return () => clearTimeout(t);
  }, [inView]);

  return (
    <section
      ref={sectionRef}
      className={`px-6 py-30 md:px-14 lg:px-20 lg:py-50 xl:px-30 ${
        tinted ? 'bg-clearlab-divider-light/20' : 'bg-clearlab-oat'
      }`}
    >
      <div className="mx-auto w-full max-w-content-xl">
        <ArticleSectionContent section={section} visible={visible} />
      </div>
    </section>
  );
}
